import { ContractEnums } from '@customer-mono/shared-types';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { colors } from '../../components';
import ButtonComponent from '../../components/button/ButtonComponent';
import { AutoPayBadgeStyles, AutopayInfoWrapper, CommonTooltip } from '../../components/global-styles/GlobalStyles';
import { SelectMenu } from '../../components/select/SelectMenu';
import CommonHeader from '../../components/ui-components/CommonHeader';
import CommonIconAvatar from '../../components/ui-components/CommonIconAvatar';
import CommonStatusChip from '../../components/ui-components/StatusChipComponent';
import { CardContainer } from '../../components/ui-components/SummaryCard';
import { useContracts } from '../../hooks/useContracts';
import { useStaleOnResume } from '../../hooks/useStaleOnResume';
import { ContractApiResponse } from '../../services/contract-services';
import { DashboardApiResponse, getDashboardData } from '../../services/dashboard-service';
import { getUserNotificationsCount } from '../../services/login-service';
import { getOpenInvoices, OpenInvoiceApiResponse } from '../../services/payment-services';
import { getProfileDetails } from '../../services/user-service';
import { RootState } from '../../store';
import { showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { setUserNotificationsCount } from '../../store/reducers/userSlice';
import { updateUserProfileDetails } from '../../store/reducers/userSlice';
import { trackContractSelected, trackMakePaymentClick } from '../../utils/google-analytics';
import {
    canMakePaymentForContract,
    formatCurrency,
    formatToUSDate,
    getCurrentPaymentDueInfo,
    shouldDisablePayNow,
} from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';

import { PaymentSummaryCard } from './DashboardStyles';

// SVG ring constants for payment progress (matches reference: thick ring, rounded ends, top→clockwise)
const RING_SIZE = 140;
const RING_CX = RING_SIZE / 2;
const RING_CY = RING_SIZE / 2;
const RING_RADIUS = 55;
const RING_STROKE_WIDTH = 25;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function handleDashboardRedirect(
    navigate: ReturnType<typeof useNavigate>,
    redirect: string,
    redirectState?: Record<string, unknown>,
) {
    if (!redirect) return;
    if (redirect === NavigationConfig.ContractDetails && redirectState?.contract) {
        const c = redirectState.contract as { AspireId?: string; Id?: string | number };
        trackContractSelected(c.AspireId || String(c.Id ?? ''), 'dashboard');
    }
    navigate(redirect, { state: redirectState });
}

function handleViewAllKeyDown(event: React.KeyboardEvent, navigate: ReturnType<typeof useNavigate>) {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        navigate(NavigationConfig.Payments);
    }
}

function selectFirstActiveContract(contractsList: ContractApiResponse[], setContractFn: (id: string) => void) {
    const first = contractsList.find(c => c.Status === ContractEnums?.ContractStatusTypes?.active);
    if (first) {
        setContractFn(first.AspireId || `Contract ${first.Id}`);
    }
}

export const DashboardHome: React.FC = () => {
    const theme = useTheme();
    const [contract, setContract] = useState('');
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isLoading = useSelector((state: RootState) => state.app.activeHTTPRequests > 0);
    const { userDetails, isLoggedIn } = useSelector((state: RootState) => state.user);
    const { contracts, fetchContracts } = useContracts();
    const [dashboardData, setDashboardData] = useState<DashboardApiResponse['data']>({
        TotalOutstandingBalance: 0,
        UpcomingPayments: 0,
        ServiceRequests: 0,
        ActiveContracts: 0,
        LastRetrievalTime: '',
    });
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const [upcomingPaymentsData, setUpcomingPaymentsData] = useState<OpenInvoiceApiResponse[]>([]);
    const initialDashboardFetch = useRef(false);
    const initialUpcomingPaymentsFetch = useRef(false);
    const impersonatedUserDetailsFetched = useRef(false);
    const initialContractsFetch = useRef(false);

    // Fetch contracts on component mount
    useEffect(() => {
        const loadContracts = async () => {
            // Always refetch once on mount so contracts are populated with current backend data.
            if (!initialContractsFetch.current) {
                initialContractsFetch.current = true;
                try {
                    const response = await fetchContracts(true);
                    if (response.statusCode === 200 && Array.isArray(response.data)) {
                        selectFirstActiveContract(response.data, setContract);
                    } else {
                        dispatch(
                            openToast({
                                show: true,
                                isAlertOpen: true,
                                message: response.message || 'Failed to Load Contracts',
                                severity: 'error',
                            }),
                        );
                    }
                } catch (err) {
                    const e = err as { response?: { data?: { message?: string } }; message?: string };
                    const errorMessage = e?.response?.data?.message || e?.message || 'Failed to Load Contracts';
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: errorMessage,
                            severity: 'error',
                        }),
                    );
                }
            } else if (contracts.length > 0 && !contract) {
                selectFirstActiveContract(contracts, setContract);
            }
        };

        loadContracts();
    }, [dispatch, contracts, contract, fetchContracts]);

    const fetchUserNotificationsCount = useCallback(async () => {
        const response = await getUserNotificationsCount();

        if (response.success) {
            dispatch(setUserNotificationsCount(response?.data?.unreadCount));
        }
    }, [dispatch]);

    const fetchDashboardData = useCallback(async (showLoader: boolean = true) => {
        const response = await getDashboardData(showLoader);
        if (response.statusCode === 200) {
            setDashboardData(response.data);
        }
    }, []);

    useEffect(() => {
        if (initialDashboardFetch.current) return;
        initialDashboardFetch.current = true;

        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchDashboardData(true);
        fetchUserNotificationsCount();
    }, [fetchDashboardData, fetchUserNotificationsCount]);

    const fetchUpcomingPayments = useCallback(async (showLoader: boolean = true) => {
        const response = await getOpenInvoices(showLoader);
        if (response.statusCode === 200) {
            setUpcomingPaymentsData(response.data);
        }
    }, []);

    useEffect(() => {
        if (initialUpcomingPaymentsFetch.current) return;
        initialUpcomingPaymentsFetch.current = true;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchUpcomingPayments(true);
    }, [fetchUpcomingPayments]);

    const fetchImpersonatedUserDetails = useCallback(async () => {
        if (!impersonation?.isImpersonating) return;
        try {
            const response = await getProfileDetails(false);
            const profileDetails = response?.data?.data || response?.data;

            if (profileDetails && typeof profileDetails === 'object') {
                dispatch(updateUserProfileDetails(profileDetails));
            }
        } catch (error) {
            console.error('Error fetching impersonated user details on dashboard:', error);
        }
    }, [dispatch, impersonation?.isImpersonating]);

    useEffect(() => {
        // TODO: Refresh impersonated user details once on dashboard entry so the header uses API-backed name data.
        if (!impersonation?.isImpersonating || impersonatedUserDetailsFetched.current) {
            return;
        }

        impersonatedUserDetailsFetched.current = true;
        fetchImpersonatedUserDetails();
    }, [impersonation?.isImpersonating, fetchImpersonatedUserDetails]);

    // Re-fetch dashboard summary, upcoming payments, and (if impersonating) profile details
    // when the tab regains focus after sitting hidden long enough to be considered stale —
    // contracts already get this treatment inside useContracts itself.
    const refetchOnResume = useCallback(async () => {
        await Promise.all([fetchDashboardData(false), fetchUpcomingPayments(false), fetchImpersonatedUserDetails()]);
    }, [fetchDashboardData, fetchUpcomingPayments, fetchImpersonatedUserDetails]);
    useStaleOnResume(refetchOnResume, isLoggedIn);

    const cardData = [
        {
            title: t('dashboard.cards.upcomingPayments'),
            value: dashboardData?.UpcomingPayments,
            icon: 'icon-open-invoice',
            redirect: NavigationConfig.Payments,
        },
        {
            title: t('dashboard.cards.activeContracts'),
            value: dashboardData?.ActiveContracts,
            icon: 'icon-contracts',
            redirect: NavigationConfig.ContractOverview,
        },
        {
            title: t('dashboard.cards.serviceRequests'),
            value: dashboardData?.ServiceRequests,
            icon: 'icon-service',
            redirect: NavigationConfig.Support,
            redirectState: { activeTab: 1 }, // Open Support Requests tab
        },
    ];
    // Filter to only active contracts
    const activeContracts = contracts.filter(
        contractItem => contractItem.Status === ContractEnums?.ContractStatusTypes?.active,
    );

    // If selected contract is not active, reset to first active contract
    useEffect(() => {
        if (contract && contracts.length > 0) {
            const activeContractsList = contracts.filter(
                contractItem => contractItem.Status === ContractEnums?.ContractStatusTypes?.active,
            );
            if (activeContractsList.length > 0) {
                const isSelectedContractActive = activeContractsList.some(
                    contractItem => contractItem.AspireId === contract || `Contract ${contractItem.Id}` === contract,
                );
                if (!isSelectedContractActive) {
                    // Selected contract is not active, switch to first active contract
                    const firstActiveContract = activeContractsList[0];
                    // eslint-disable-next-line react-hooks/set-state-in-effect
                    setContract(firstActiveContract.AspireId || `Contract ${firstActiveContract.Id}`);
                }
            }
        }
    }, [contract, contracts]);

    // Get selected contract data from active contracts
    const selectedContract = activeContracts.find(
        contractItem => contractItem.AspireId === contract || `Contract ${contractItem.Id}` === contract,
    );
    // Calculate payment percentage based on PaymentsReceived and NumberOfPayments (same logic as activeList.tsx)
    const paymentsReceived_ = selectedContract?.PaymentsReceived ?? 0;
    const numberOfPayments_ = selectedContract?.NumberOfPayments ?? 0;
    const paymentPercentage =
        paymentsReceived_ > 0 && numberOfPayments_ > 0 ? (paymentsReceived_ / numberOfPayments_) * 100 : 0;

    // Calculate total paid and total due from selected contract
    const totalDue = Number(selectedContract?.RemainingPrincipal) || Number(selectedContract?.OutstandingBalance) || 0;
    const paymentsReceived = Number(selectedContract?.PrincipleAmount ?? 0) - totalDue;
    const totalPaid = paymentsReceived;

    // Progress ring: paid segment (dark blue) from top clockwise, remainder light grey; rounded ends
    const paidFillColor = paymentPercentage > 0 ? colors.primaryLight[100] : colors.greyPreset[20];
    const progressOffset = RING_CIRCUMFERENCE - (paymentPercentage / 100) * RING_CIRCUMFERENCE;

    const showAutoPaySuspendedBanner =
        selectedContract?.AutoPay &&
        selectedContract?.AutoPayResumeDate &&
        dayjs(selectedContract.AutoPayResumeDate).isAfter(dayjs(), 'day');
    const autoPayResumeDateFormatted = selectedContract?.AutoPayResumeDate
        ? dayjs(selectedContract.AutoPayResumeDate).format('MM/DD/YYYY')
        : '';

    const handlePayAll = () => {
        if (activeContracts.length > 0) {
            // Track make payment click from dashboard
            trackMakePaymentClick('Dashboard Header');

            const allContractIds = activeContracts.map(
                contractItem => contractItem.AspireId || `Contract ${contractItem.Id}`,
            );
            navigate(NavigationConfig.DashboardMakePayment, {
                state: { selectedContractIds: allContractIds, payAll: true, fromDashboard: true },
            });
        }
    };

    // Pay All button disabled when no contract can make payment (same logic as Pay Now: consumer loan can always pay; others need open invoices)
    const allContractsDisabled =
        activeContracts.length === 0 ||
        activeContracts.every(c => !canMakePaymentForContract(c, upcomingPaymentsData ?? [], activeContracts));

    console.log(' showAutoPaySuspendedBanner ', showAutoPaySuspendedBanner);

    return (
        <Stack gap={3} p={3}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                <CommonHeader
                    mainTitle={
                        (userDetails?.FirstName && userDetails?.LastName
                            ? `${userDetails.FirstName} ${userDetails.LastName}`
                            : userDetails?.Name) as string | undefined
                    }
                />
                <Stack direction={'row'} gap={2}>
                    <ButtonComponent
                        variant="contained"
                        sx={{
                            width: 'fit-content',
                            pointerEvents: allContractsDisabled ? 'none' : 'auto',
                            cursor: allContractsDisabled ? 'not-allowed' : 'pointer',
                        }}
                        onClick={handlePayAll}
                        disabled={allContractsDisabled}
                    >
                        {t('dashboard.header.makePayment')}
                    </ButtonComponent>
                </Stack>
            </Stack>
            <Grid container spacing={2}>
                {cardData.map((card, index) => (
                    <Grid size={{ xs: 12, md: 6, lg: 4 }} key={index}>
                        <CardContainer
                            onClick={
                                card.redirect
                                    ? () => handleDashboardRedirect(navigate, card.redirect, card.redirectState)
                                    : undefined
                            }
                            hasOnClick={!!card.redirect}
                            sx={{
                                pointerEvents: 'auto',
                                cursor: 'pointer',
                            }}
                        >
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                <Stack direction={'row'} alignItems={'center'} gap={2}>
                                    <CommonIconAvatar icon={card.icon} />
                                    <Stack direction={'row'} alignItems={'center'} gap={1}>
                                        <Typography variant="h3" fontWeight={700}>
                                            {card.value}
                                        </Typography>
                                        <Typography variant="body1" color="textSecondary">
                                            {card.title}
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <IconButton
                                    size="small"
                                    color="inherit"
                                    aria-label={`View ${card.title}`}
                                    sx={{ p: 0 }}
                                >
                                    <CommonIconAvatar
                                        icon="icon-arrow-up"
                                        iconPadding={1}
                                        iconBorderRadius={4}
                                        iconSize={16}
                                        iconWeight={600}
                                        iconRotate="rotate(45deg)"
                                        iconColor={theme.palette.text.primary}
                                        iconBgColor={colors.primaryLight[15]}
                                    />
                                </IconButton>
                            </Stack>
                        </CardContainer>
                    </Grid>
                ))}
            </Grid>
            <Stack>
                <PaymentSummaryCard gap={3}>
                    <Stack
                        direction={'row'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        flexWrap={'wrap'}
                        gap={1}
                    >
                        <Typography variant="h6" color="textPrimary">
                            {t('dashboard.sections.paymentProgress')}
                        </Typography>
                        <Stack direction={'row'} alignItems={'center'} gap={2}>
                            {selectedContract?.AutoPay && (
                                <AutoPayBadgeStyles direction="row" alignItems="center">
                                    <span className="icon-check" />
                                    <Typography variant="subtitle2">{t('common.AutoPay')}</Typography>
                                </AutoPayBadgeStyles>
                            )}
                            <Stack className="selectMenuWrapper">
                                <SelectMenu
                                    label="Contract"
                                    value={contract}
                                    onChange={setContract}
                                    options={
                                        activeContracts?.length > 0
                                            ? activeContracts?.map(
                                                  contractItem =>
                                                      contractItem?.AspireId || `Contract ${contractItem?.Id}`,
                                              )
                                            : ['No active contracts available']
                                    }
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                    {showAutoPaySuspendedBanner && (
                        <AutopayInfoWrapper direction="row" alignItems="center">
                            {
                                <>
                                    <span className="icon-info-circle" />
                                    <Typography variant="subtitle2" component="h3">
                                        {t('common.AutopayInfoNoteStart')} {autoPayResumeDateFormatted}{' '}
                                        {t('common.AutopayInfoNoteEnd')}
                                    </Typography>
                                </>
                            }
                        </AutopayInfoWrapper>
                    )}
                    <Stack>
                        <Grid container spacing={1.5} sx={{ alignItems: 'stretch' }}>
                            <Grid size={{ xs: 12, md: 6, lg: 3.6, xl: 3 }}>
                                <Stack
                                    direction={'row'}
                                    className="paymentCardWraper"
                                    sx={{ gap: 2, height: '100%', alignItems: 'center' }}
                                >
                                    {/* Payment progress ring (SVG: rounded segment ends, top→clockwise) */}
                                    <Stack sx={{ width: RING_SIZE, height: RING_SIZE, position: 'relative' }}>
                                        <Box
                                            component="svg"
                                            viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                                            sx={{ position: 'absolute', width: '100%', height: '100%' }}
                                        >
                                            {/* Background track (full ring, light grey) */}
                                            <circle
                                                cx={RING_CX}
                                                cy={RING_CY}
                                                r={RING_RADIUS}
                                                fill="none"
                                                stroke={colors.greyPreset[20]}
                                                strokeWidth={RING_STROKE_WIDTH}
                                            />
                                            {/* Paid segment (dark blue, rounded ends, from top clockwise) */}
                                            <circle
                                                cx={RING_CX}
                                                cy={RING_CY}
                                                r={RING_RADIUS}
                                                fill="none"
                                                stroke={paidFillColor}
                                                strokeWidth={RING_STROKE_WIDTH}
                                                strokeDasharray={RING_CIRCUMFERENCE}
                                                strokeDashoffset={progressOffset}
                                                strokeLinecap="round"
                                                transform={`rotate(-90 ${RING_CX} ${RING_CY})`}
                                            />
                                        </Box>
                                        <Stack className="chartPercentage">
                                            <Typography
                                                variant="h5"
                                                component="h3"
                                                color="textPrimary"
                                                fontWeight={600}
                                            >
                                                {paymentPercentage.toFixed(1)}%
                                            </Typography>
                                            <Typography variant="body2" component="h4" color="textSecondary">
                                                Paid
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    {/* Legend Section */}
                                    <Stack
                                        sx={{ alignItems: 'start', justifyContent: 'center', gap: 1, display: 'grid' }}
                                    >
                                        {!selectedContract?.IsLoan && (
                                            <Typography variant="body1" color="textSecondary">
                                                {selectedContract?.PaymentsReceived} {t('contracts.list.of')}{' '}
                                                {selectedContract?.NumberOfPayments} {t('contracts.list.paymentsMade')}
                                            </Typography>
                                        )}
                                        {selectedContract?.IsLoan && (
                                            <Stack direction="row" overflow={'hidden'} alignItems={'center'}>
                                                <Typography variant="body1" color="textSecondary">
                                                    Principal
                                                </Typography>
                                                <CommonTooltip
                                                    placement="top"
                                                    title={formatCurrency(
                                                        selectedContract?.PrincipleAmount?.toString(),
                                                    )}
                                                >
                                                    <Typography variant="subtitle1" component="h4" pl={1} noWrap>
                                                        {formatCurrency(selectedContract?.PrincipleAmount?.toString())}
                                                    </Typography>
                                                </CommonTooltip>
                                            </Stack>
                                        )}
                                        {selectedContract?.IsLoan && (
                                            <Stack direction="row" overflow={'hidden'} alignItems={'center'}>
                                                <Typography variant="body1" color="textSecondary">
                                                    Paid
                                                </Typography>
                                                <CommonTooltip placement="top" title={formatCurrency(totalPaid)}>
                                                    <Typography variant="subtitle1" component="h4" pl={1} noWrap>
                                                        {formatCurrency(totalPaid)}
                                                    </Typography>
                                                </CommonTooltip>
                                            </Stack>
                                        )}
                                        {selectedContract?.IsLoan && (
                                            <Stack direction="row" overflow={'hidden'} alignItems={'center'}>
                                                <Typography variant="body1" color="textSecondary">
                                                    Remaining
                                                </Typography>
                                                <CommonTooltip placement="top" title={formatCurrency(totalDue)}>
                                                    <Typography variant="subtitle1" component="h4" pl={1} noWrap>
                                                        {formatCurrency(totalDue)}
                                                    </Typography>
                                                </CommonTooltip>
                                            </Stack>
                                        )}
                                    </Stack>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 2.1, xl: 2.25 }}>
                                <Stack
                                    className="paymentCardWraper"
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                    gap={1}
                                    sx={{ height: '100%' }}
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        {t('contracts.details.pastDue')}
                                    </Typography>
                                    <Typography variant="h3" fontWeight={600}>
                                        {formatCurrency(selectedContract?.PastDue ?? '')}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 2.1, xl: 2.25 }}>
                                <Stack
                                    className="paymentCardWraper"
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                    gap={1}
                                    sx={{ height: '100%' }}
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        {t('contracts.details.currentPaymentDue')}
                                    </Typography>
                                    <Typography variant="h3" fontWeight={600} color="textPrimary">
                                        {formatCurrency(
                                            Number(selectedContract?.PastDue) > 0
                                                ? (selectedContract?.PastDue ?? '')
                                                : getCurrentPaymentDueInfo(selectedContract).amount,
                                        )}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {selectedContract?.CurrentPaymentDate
                                            ? `On ${formatToUSDate(selectedContract.CurrentPaymentDate)}`
                                            : 'N/A'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 2.1, xl: 2.25 }}>
                                <Stack
                                    className="paymentCardWraper"
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                    gap={1}
                                    sx={{ height: '100%' }}
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        Next Payment
                                    </Typography>
                                    <Typography variant="h3" fontWeight={600} color="textPrimary">
                                        {formatCurrency(selectedContract?.NextPaymentAmount ?? '')}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {selectedContract?.NextPaymentDate
                                            ? `On ${formatToUSDate(selectedContract.NextPaymentDate)}`
                                            : 'N/A'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 2.1, xl: 2.25 }}>
                                <Stack
                                    className="paymentCardWraper"
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                    gap={1}
                                    sx={{ height: '100%' }}
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        Last Payment
                                    </Typography>
                                    <Typography variant="h3" fontWeight={600} color="textPrimary">
                                        {formatCurrency(selectedContract?.LastPaymentAmount ?? '')}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        On{' '}
                                        {selectedContract?.LastPaymentDate
                                            ? formatToUSDate(selectedContract.LastPaymentDate)
                                            : 'N/A'}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Stack>
                </PaymentSummaryCard>
            </Stack>

            <PaymentSummaryCard>
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={1.5}>
                    <Typography variant="h6" color="textPrimary">
                        {t('dashboard.sections.upcomingPayments')}
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{ cursor: 'pointer', color: 'textPrimary' }}
                        onClick={() => navigate(NavigationConfig.Payments)}
                        tabIndex={0}
                        onKeyDown={event => handleViewAllKeyDown(event, navigate)}
                    >
                        {t('dashboard.sections.viewAll')}
                    </Typography>
                </Box>

                <Stack gap={1.5}>
                    {upcomingPaymentsData?.length > 0 &&
                        upcomingPaymentsData?.slice(0, 3)?.map(payment => {
                            const disablePayNow = shouldDisablePayNow(payment?.aspireContractId ?? '', contracts);
                            return (
                                <Stack
                                    className="contentWrapper"
                                    direction={'row'}
                                    alignItems={'center'}
                                    justifyContent={'space-between'}
                                    key={payment?.invoiceNumber || payment?.aspireContractId}
                                    flexWrap={'wrap'}
                                    gap={1}
                                >
                                    <Stack spacing={1.5} direction={'row'} alignItems={'center'}>
                                        <CommonIconAvatar icon="icon-open-invoice" />
                                        <Box>
                                            <Stack direction={'row'} alignItems={'center'} gap={1.5}>
                                                <Typography variant="body2" component="span" color="textSecondary">
                                                    {t('contracts.details.contractId')}
                                                    <Typography
                                                        variant="subtitle1"
                                                        component="span"
                                                        pl={1}
                                                        color="textPrimary"
                                                    >
                                                        {payment?.aspireContractId}
                                                    </Typography>
                                                </Typography>
                                                {/* Overdue chip */}
                                                {payment?.duedate && new Date(payment?.duedate) < new Date() && (
                                                    <CommonStatusChip
                                                        label={t('dashboard.alerts.overdue')}
                                                        chipBgColor={colors.errorPreset[10]}
                                                    />
                                                )}
                                            </Stack>
                                            <Typography variant="caption" color="textSecondary">
                                                {payment?.duedate ? formatToUSDate(payment?.duedate) : 'N/A'}
                                            </Typography>
                                            {/* Autopay badge for contract */}
                                        </Box>
                                    </Stack>
                                    <Stack
                                        spacing={2}
                                        direction={'row'}
                                        alignItems={'center'}
                                        justifyContent={'end'}
                                        width={{ xs: '100%', sm: 'auto' }}
                                    >
                                        {/* {showAutoPaySuspendedBanner && <AutopayInfoWrapper direction="row" alignItems="center" sx={{ p: 1, border: 'none' }}>
                                            <span className="icon-info-circle" />
                                            <Typography variant="subtitle2">
                                                {t('common.AutopayIsAlreadyEnabled')}
                                            </Typography>
                                        </AutopayInfoWrapper>} */}
                                        <Typography variant="subtitle1" color="textPrimary">
                                            {formatCurrency(payment?.amount ?? '')}
                                        </Typography>
                                        {/* <CommonTooltip
                                            title={disablePayNow ? t('dashboard.payments.upcomingPayments.autopayEnabledTooltip') : ''}
                                            disableHoverListener={!disablePayNow}
                                            arrow
                                        > */}
                                        <span>
                                            <ButtonComponent
                                                variant="contained"
                                                sx={{
                                                    width: 'fit-content',
                                                    minWidth: 100,
                                                    fontWeight: 600,
                                                    transition: 'all 0.2s ease',
                                                    pointerEvents: disablePayNow ? 'none' : 'auto',
                                                    cursor: disablePayNow ? 'not-allowed' : 'pointer',
                                                    '&:hover:not(:disabled)': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                    },
                                                }}
                                                disabled={disablePayNow}
                                                onClick={() => {
                                                    if (!disablePayNow) {
                                                        dispatch(showLoader());
                                                        const invoiceAmount = Number(
                                                            payment?.amount ?? payment?.Amount ?? 0,
                                                        );
                                                        navigate(NavigationConfig.DashboardMakePayment, {
                                                            state: {
                                                                selectedContractIds: [payment?.aspireContractId],
                                                                fromDashboard: true,
                                                                singleInvoiceOverrides: {
                                                                    [payment?.aspireContractId ?? '']: {
                                                                        dueAmount: invoiceAmount.toFixed(2),
                                                                        scheduledAmount: '0.00',
                                                                    },
                                                                },
                                                            },
                                                        });
                                                    }
                                                }}
                                            >
                                                {t('dashboard.alerts.payNow')}
                                            </ButtonComponent>
                                        </span>
                                        {/* </CommonTooltip> */}
                                    </Stack>
                                </Stack>
                            );
                        })}
                    {upcomingPaymentsData?.length === 0 && !isLoading && (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={150}>
                            <Typography variant="h6" component="h1" color="textSecondary" textAlign={'center'}>
                                {t('common.NoRecordsAvailable')}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </PaymentSummaryCard>
        </Stack>
    );
};
